import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import Echo from 'laravel-echo';
import { environment } from 'environments/environment';
import Pusher, { Channel } from 'pusher-js';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { AuthService } from './auth/auth.service';

(window as any).Pusher = Pusher;

@Injectable({
    providedIn: 'root',
})
export class EchoService implements OnDestroy
{
    private _echo: Echo<any> | null = null;
    private _activeChannels: Map<string, any> = new Map();
    private _isConnected$ = new BehaviorSubject<boolean>(false);
    private _destroy$ = new Subject<void>();

    constructor(
            private _authService: AuthService,
    ) {
        // EchoService initialized
    }

    public channel(channel: string) {
        const echoChannel = this.getEchoInstance().channel(channel);
        this._activeChannels.set(channel, { type: 'public', channel: echoChannel });
        return echoChannel;
    }

    public private(channel: string) {
        const echoChannel = this.getEchoInstance().private(channel);
        this._activeChannels.set(channel, { type: 'private', channel: echoChannel });
        return echoChannel;
    }

    public leave(channel: string) {
        try {
            const result = this.getEchoInstance().leave(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            console.error(`EchoService: Error leaving channel ${channel}:`, error);
            throw error;
        }
    }

    public leaveChannel(channel: string) {
        try {
            const result = this.getEchoInstance().leaveChannel(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            console.error(`EchoService: Error leaving channel ${channel}:`, error);
            throw error;
        }
    }

    public getSocketId() {
        if (this._echo && this._echo.socketId()) {
            return this._echo.socketId();
        }
        return null;
    }

    /**
     * Get list of active channels for debugging
     */
    public getActiveChannels(): string[] {
        return Array.from(this._activeChannels.keys());
    }

    /**
     * Get detailed information about active channels
     */
    public getChannelDetails(): any[] {
        return Array.from(this._activeChannels.entries()).map(([name, details]) => ({
            name,
            type: details.type,
            hasChannel: !!details.channel
        }));
    }

    /**
     * Force disconnect and destroy the Echo instance
     * Useful for testing and org changes
     */
    public destroy(): void {
        // Leave all active channels first
        const channelsToLeave = Array.from(this._activeChannels.keys());

        channelsToLeave.forEach(channelName => {
            try {
                this.leave(channelName);
            } catch (error) {
                console.error(`EchoService: Error leaving channel ${channelName} during destroy:`, error);
            }
        });

        // Disconnect and destroy Echo instance
        if (this._echo) {
            try {
                this._echo.disconnect();
            } catch (error) {
                console.error('EchoService: Error disconnecting Echo:', error);
            }
            this._echo = null;
        }

        this._activeChannels.clear();
        this._isConnected$.next(false);
    }

    ngOnDestroy(): void {
        this.destroy();
        this._destroy$.complete();
        this._isConnected$.complete();
    }

    /**
     * Manual cleanup method for testing purposes
     * (Root services' ngOnDestroy is never called automatically)
     */
    public manualCleanup(): void {
        this.destroy();
    }

    private getEchoInstance() {
        // TODO - fix this so it works on org change
        if (!this._echo) {

            const customAuthorizer = (channel: Channel, options: any): { authorize: Function } => {
                return {
                    authorize: async (socketId: string, callback: Function): Promise<void> => {
                        try {
                            const response = await fetch(environment.LegFiCoreApi + '/broadcasting/auth', {
                                method: 'POST',
                                body: JSON.stringify({
                                    socket_id: socketId,
                                    channel_name: channel.name,
                                }),
                                credentials: 'include',
                                headers: {
                                    'Accept': 'application/json',
                                    'Content-Type': 'application/json',
                                    'Authorization': 'Bearer ' + LegFiJwtService.raw(),
                                    'X-XSRF-TOKEN': this._authService.getCookie('XSRF-TOKEN'),
                                    'X-Legfi-Site-Id': environment.SiteId.toString(),
                                }
                            });

                            const data = await response.json();

                            // Echo expects these callback signatures in the case of success or failure
                            if (data.auth) {
                                callback(null, data);
                            } else {
                                console.error(`EchoService: Authorization failed for channel: ${channel.name}`);
                                callback(new Error('Authorization failed'), null);
                            }
                        } catch (error) {
                            // Handle fetch errors
                            console.error(`EchoService: Authorization error for channel ${channel.name}:`, error);
                            callback(error, null);
                        }
                    }
                };
            };

            this._echo = new Echo({
                broadcaster: 'pusher',
                key: environment.PusherAppKey,
                cluster: environment.PusherAppCluster,
                forceTLS: true,
                host: environment.LegFiCoreApi,
                authorizer: customAuthorizer,
            });

            // Set up connection monitoring
            this.setupConnectionMonitoring();
        }
        return this._echo;
    }

    private setupConnectionMonitoring(): void {
        if (this._echo?.connector?.socket) {
            this._echo.connector.socket.on('connect', () => {
                this._isConnected$.next(true);
            });

            this._echo.connector.socket.on('disconnect', () => {
                this._isConnected$.next(false);
            });

            this._echo.connector.socket.on('error', (error: any) => {
                console.error('EchoService: WebSocket error:', error);
            });
        }
    }
}
